const SOURCES = [
  {
    name: "Google Chrome",
    badge: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    acc: "text-sky-300",
    method: "DPAPI + AES-GCM decryption of Login Data SQLite",
    steps: [
      "Locate Chrome profile directory via LOCALAPPDATA",
      "Read Local State file and extract encrypted_key",
      "Decrypt the app-bound encryption key using Windows DPAPI (CryptUnprotectData)",
      "Open Login Data SQLite and read encrypted_value column",
      "Decrypt each password with AES-GCM using the recovered key",
      "Import username, password, and origin URL into the vault",
    ],
    note: "Chrome 127+ uses app-bound encryption — the import handles both legacy DPAPI-only and the newer key format.",
  },
  {
    name: "Microsoft Edge",
    badge: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40",
    acc: "text-indigo-300",
    method: "Same as Chrome — Chromium-based, identical Login Data format",
    steps: [
      "Locate Edge profile directory",
      "Same DPAPI + AES-GCM decryption pipeline as Chrome",
      "Edge uses the same Chromium Login Data SQLite schema",
    ],
    note: "Edge shares Chrome's credential storage format — the same adapter handles both.",
  },
  {
    name: "Firefox",
    badge: "bg-orange-500/20 text-orange-300 border-orange-500/40",
    acc: "text-orange-300",
    method: "NSS key3.db / logins.json decryption",
    steps: [
      "Locate Firefox profile directory via profiles.ini",
      "Read logins.json for encrypted credential entries",
      "Use NSS (Network Security Services) to decrypt the master key from key4.db",
      "Decrypt each encrypted username and password field",
      "Import into the SecureVault SQLite database",
    ],
    note: "Firefox uses Mozilla's NSS library for credential encryption — separate decryption path from Chromium.",
  },
  {
    name: "Generic CSV",
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    acc: "text-emerald-300",
    method: "Column-mapped CSV import with format auto-detection",
    steps: [
      "Load CSV file from user-selected path",
      "Auto-detect format by inspecting header row",
      "Map columns for Bitwarden, KeePass, 1Password, and generic exports",
      "Preview import candidates before committing",
      "Encrypt and write each credential to the local SQLite vault",
    ],
    note: "Supports Bitwarden, KeePass, 1Password, and any CSV with name/url/username/password columns.",
  },
];

export default function ImportEngine() {
  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">IMPORT ENGINE</p>
        <h4 className="mt-1 text-sm font-semibold text-white">Browser password import pipeline</h4>
        <p className="mt-1 text-xs text-slate-400">
          A tabbed import wizard handles Chrome, Edge, Firefox, and generic CSV exports. Browser imports decrypt credentials directly from the browser's own local storage — no export file needed.
        </p>
      </div>

      <div className="space-y-2">
        {SOURCES.map((s) => (
          <details key={s.name} className="group rounded-xl border border-white/10 bg-white/5">
            <summary className="flex cursor-pointer select-none items-center gap-3 p-3 list-none">
              <span className={`rounded border px-2 py-0.5 font-mono text-[9px] font-bold ${s.badge}`}>{s.name}</span>
              <span className="text-[11px] text-slate-400">{s.method}</span>
              <svg className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-500 transition group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </summary>
            <div className="border-t border-white/10 px-3 pb-3 pt-2 space-y-2">
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <p className={`mb-2 font-mono text-[9px] tracking-[0.15em] ${s.acc}`}>DECRYPTION STEPS</p>
                <ol className="space-y-1.5">
                  {s.steps.map((step, i) => (
                    <li key={step} className="flex gap-2 text-[11px] text-slate-400">
                      <span className={`shrink-0 font-mono text-[9px] font-bold ${s.acc}`}>{String(i + 1).padStart(2, "0")}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              {s.note && (
                <p className={`rounded-lg border px-2.5 py-2 text-[11px] ${s.badge}`}>{s.note}</p>
              )}
            </div>
          </details>
        ))}
      </div>

      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
        <p className="font-mono text-[10px] tracking-[0.2em] text-amber-400">SECURITY NOTE</p>
        <p className="mt-1 text-xs text-slate-300">
          All decryption happens locally in memory — no credential data is sent to any server, logged to disk, or written anywhere outside the encrypted vault. The import preview shows credentials before committing so the user can deselect specific entries.
        </p>
      </div>
    </div>
  );
}
